/**
 *
 * @module playerAdapter
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/media.md
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/stream-sample.md
 * @see https://github.bamtech.co/fed-core/browser-sdk/blob/master/docs/reference/PlayerEvents.md
 * @see https://github.bamtech.co/fed-core/browser-sdk/blob/master/docs/reference/PlayerProperties.md
 *
 */

import Check from 'check-types';
import Exception from './../services/exception/entry';
import Drm from './../drm/entry';
import PlaybackMetricsProvider from './playbackMetricsProvider';

const DrmProvider = Drm.DrmProvider;

const InvalidArgumentException = Exception.InvalidArgumentException;

const {
    assigned,
    instanceStrict,
    nonEmptyString
} = Check.assert;

/**
 *
 * @abstract
 * @class PlayerAdapter
 * @implements {IPlayerAdapter}
 * @extends {PlaybackMetricsProvider}
 * @desc Interface used to communicate with the media player.
 *
 */
export default class PlayerAdapter extends PlaybackMetricsProvider {

    /**
     *
     * @param {NativePlayer} nativePlayer
     *
     */
    constructor(nativePlayer) {

        const errorMessage = {
            ctor: 'PlayerAdapter(nativePlayer)',
            get nativePlayer() {
                return `${this.ctor} needs NativePlayer instance`;
            }
        };

        assigned(nativePlayer, errorMessage.nativePlayer, InvalidArgumentException);

        super();

        /**
         *
         * @access public
         * @type {String}
         * @since 2.0.0
         *
         */
        this.playlistUri = '';

        /**
         *
         * @access private
         * @type {NativePlayer}
         * @since 2.0.0
         *
         */
        this.nativePlayer = nativePlayer;

        /**
         *
         * @access private
         * @type {PlaybackEventListener}
         * @since 2.0.0
         *
         */
        this.listener = null;

        /**
         *
         * @access private
         * @type {SDK.Drm.DrmProvider}
         * @since 3.2.0
         *
         */
        this.drmProvider = null;

        /**
         *
         * @access private
         * @type {String}
         *
         */
        this.accessToken = null;

        /**
         *
         * @access private
         * @type {Object}
         * @since 2.0.0
         *
         */
        this.boundHandlers = {};

        /**
         *
         * @access private
         * @type {Number}
         * @since 2.0.0
         * @desc Value provided if the metric (playhead or bitrate) is not available.
         *
         */
        this.unavailableMetric = null;

        /**
         *
         * @access prviate
         * @type {Object}
         * @since 3.6.0
         * @desc used to store adEngine data which is populated from the playlist service in playerAdapter.prepare()
         *
         */
        this.adEngineData = {};

    }

    /**
     *
     * @access public
     * @param {String} playlistUri - The URL to be used during playback.
     * @desc Callback used when prepare has been called (usually via the {PlaybackSession}).
     * Sets the source URI on the {NativePlayer} instance.
     * @returns {Promise<Void>}
     *
     */
    setSource(playlistUri) {

        const errorMessage = {
            method: 'PlayerAdapter.setSource(playlistUri)',
            get playlistUri() {
                return `${this.method} playlistUri should be a String with length`;
            }
        };

        nonEmptyString(playlistUri, errorMessage.playlistUri, InvalidArgumentException);

        return new Promise((resolve, reject) => {
            return reject(new Error(`${errorMessage.method} - not-implemented`));
        });

    }

    /**
     *
     * @access public
     * @param {SDK.Drm.DrmProvider} drmProvider
     * @returns {Promise<Void>}
     *
     */
    setDrmProvider(drmProvider) {

        const errorMessage = {
            method: 'PlayerAdapter.setDrmProvider(drmProvider)',
            get drmProvider() {
                return `${this.method} needs SDK.Drm.DrmProvider instance`;
            }
        };

        instanceStrict(drmProvider, DrmProvider, errorMessage.drmProvider, InvalidArgumentException);

        return new Promise((resolve, reject) => {
            return reject(new Error(`${errorMessage.method} - not-implemented`));
        });

    }

    /**
     *
     * @access public
     * @param {String} accessToken
     * @desc Notifies the player adapter that the access token has been updated.
     * Used for key service authentication.
     * @returns {Promise<Void>}
     *
     */
    onAccessChanged(accessToken) {

        if (Check.string(accessToken) && Check.nonEmptyString(accessToken)) {
            this.accessToken = accessToken;
        }

        return Promise.resolve();

    }

    /**
     *
     * @access public
     * @param {AuthorizationException} exception
     * @desc Notifies the application that the token refresh has failed.
     * Key decryption will fail on next attempt.
     * @returns {Promise<AuthorizationException>}
     *
     */
    onAccessFailed(exception) {

        return Promise.resolve(exception);

    }

    /**
     *
     * @access public
     * @desc Resets player adapter state. Removes all playback listeners.
     * @returns {Promise<Void>}
     *
     */
    clean() {

        const method = 'PlayerAdapter.clean()';

        return new Promise((resolve, reject) => {
            return reject(new Error(`${method} - not-implemented`));
        });

    }

    /* #region private: */

    /**
     *
     * @access private
     * @param {Object} adEngineData
     * @desc overwrites local adEngine object in playbackSession.prepare()
     * @note used in xhrCallbacks for bam-hls and other platforms that need to reuse adEngine data
     *
     */
    setAdEngineData(adEngineData) {

        this.adEngineData = adEngineData;

    }

    /**
     *
     * @access private
     * @since 2.0.0
     * @returns {Promise<Void>}
     *
     */
    onPlay() {

        const { listener } = this;

        return listener.onPlay(this);

    }

    /**
     *
     * @access private
     * @since 2.0.0
     * @returns {Promise<Void>}
     *
     */
    onPause() {

        const { listener } = this;

        return listener.onPause(this);

    }

    /**
     *
     * @access private
     * @since 2.0.0
     * @returns {Promise<Void>}
     *
     */
    onError() {

        const { listener } = this;

        return listener.onError(this);

    }

    /**
     *
     * @access private
     * @since 2.0.0
     * @returns {Promise<Void>}
     *
     */
    onPlayedToCompletion() {

        const { listener } = this;

        return listener.onPlayedToCompletion(this);

    }

}



// WEBPACK FOOTER //
// ./src/media/playerAdapter.js